import { Link, useLocation } from 'react-router-dom'
import { DefinitionList } from '@/components/data'
import { buttonShape } from '@/components/primitives'
import { DeadEnd } from '@/screens/DeadEnd/DeadEnd'

/**
 * An address that matches nothing.
 *
 * Distinct from `NoAccess`, and kept distinct on purpose: one means the screen
 * is not there and the other means it is there and not yours. Collapsing them
 * into a single "something went wrong" is how somebody spends ten minutes
 * asking an administrator for a permission that would not have helped.
 *
 * The mobile app sends an unknown path to the conversation screen, because on a
 * phone there is one thing to do and no address bar to have mistyped. A console
 * has thirty screens and people paste links to them, so a wrong one has to say
 * so rather than quietly landing somewhere plausible.
 *
 * ## It shows the address
 *
 * Which sounds like decoration and is the most useful thing on the screen. Most
 * arrivals here are a link that was truncated by a chat client, an id that lost
 * its last character in a copy, or a path from a different environment. All
 * three are visible the moment the address is printed and invisible while it is
 * only in the address bar, which nobody reads once a page has loaded.
 *
 * ## It suggests a closed record rather than a broken console
 *
 * The commonest true reason for a 404 in this product is not a typo: it is a
 * project, case or broadcast that was closed or deleted since the link was
 * sent, and the second commonest is a record belonging to another organization,
 * which the platform model reports as not found rather than as forbidden
 * because confirming that an id exists is itself a small leak. That second one
 * is deliberately not spelled out here, since spelling it out would undo the
 * discretion it exists for; "may have been closed, or may belong to another
 * organization" says enough for somebody to go and ask.
 */
export function NotFound() {
  const location = useLocation()
  const address = location.pathname + location.search

  return (
    <DeadEnd
      title="There is nothing at this address"
      facts={<DefinitionList items={[{ term: 'You asked for', description: address }]} />}
      actions={
        <>
          <Link className={buttonShape({ variant: 'primary' })} to="/dashboard">
            Back to Today
          </Link>
          <Link to="/projects">Browse projects</Link>
        </>
      }
    >
      <p>
        The link may be out of date, or the project, case or broadcast it pointed at may have been
        closed since it was sent.
      </p>
      <p>
        If a colleague sent it, check that the whole address arrived: a link that has been broken
        across two lines by a chat client loses its ending and lands here.
      </p>
    </DeadEnd>
  )
}
